// Copyright (c) StrangeBee 2023

import { ObjectTypeV1, UserOutput, CustomStatusOutput } from 'thehive-sdk';
import { HTMLColors, PAPColors, SeverityColors, TLPColors } from '@Constants/colors';
import { FormInstance } from 'antd';
import { useListAllFields } from '@Queries/describeQueries';
import { useEffect, useMemo } from 'react';
import useListQuery from '@Hooks/useListQuery';
import client from '@Client';
import { computeListSelector } from '@Organisms/CustomStatus/selector';
import { ColorsType } from '@Types';

interface UseCustomizeParams {
    entity?: ObjectTypeV1;
    field?: string;
    form: FormInstance<any>;
    userList?: UserOutput[];
}

export const useCustomize = ({ entity, field, form }: UseCustomizeParams): void => {
    const { data: describeData } = useListAllFields();

    const { data: caseStatusList = [] } = useListQuery<CustomStatusOutput[]>({
        controller: client.CustomStatusController,
        queryParams: {
            selector: computeListSelector({ entity: ObjectTypeV1.CASE_STATUS }),
            sort: ['+value'],
        },
        manual: true,
        fetchOnStart: field === 'status' && entity === ObjectTypeV1.CASE,
    });

    const { data: alertStatusList = [] } = useListQuery<CustomStatusOutput[]>({
        controller: client.CustomStatusController,
        queryParams: {
            selector: computeListSelector({ entity: ObjectTypeV1.ALERT_STATUS }),
            sort: ['+value'],
        },
        manual: true,
        fetchOnStart: field === 'status' && entity === ObjectTypeV1.ALERT,
    });

    const statusList = useMemo(
        () => (entity === ObjectTypeV1.CASE ? caseStatusList : alertStatusList),
        [caseStatusList, alertStatusList, entity],
    );

    const getColor = (field?: string, index?: number): string | undefined => {
        const colorMap: Record<string, ColorsType[]> = {
            tlp: TLPColors,
            pap: PAPColors,
            severity: SeverityColors,
        };

        if (!field || index === undefined || !(field in colorMap)) {
            return undefined;
        }

        const colorName = colorMap[field][index].toUpperCase();

        return HTMLColors[colorName as keyof typeof HTMLColors];
    };

    useEffect(() => {
        if (!entity || !field) {
            return;
        }

        if (!describeData) {
            return;
        }

        if (!describeData[entity]) {
            return;
        }

        const describeField = describeData[entity].attributes.find(({ name }) => name === field);

        if (!describeField) {
            return;
        }

        if (!describeField.values.length) {
            form.setFieldsValue({
                customize: [],
            });
            return;
        }

        if (field === 'status' && [ObjectTypeV1.CASE, ObjectTypeV1.ALERT].includes(entity)) {
            form.setFieldsValue({
                customize: statusList.map(({ value, colour }) => ({
                    name: value,
                    label: value,
                    color: colour,
                })),
            });
            return;
        } else {
            form.setFieldsValue({
                customize: [],
            });
        }

        form.setFieldsValue({
            customize: describeField.labels.map((label, index) => ({
                name: `${index < describeField.values.length ? describeField.values[index] : ''}`,
                label,
                color: getColor(field, index),
            })),
        });
    }, [entity, field, describeData, form, statusList]);
};
